// frontend/app/components/journeys/present/JourneyPresentMode.js
//
// Teacher classroom presentation for Saras Journeys / STEM Missions.
//
// Design rule: presentation respects the owner of the Journey that is already
// loaded. A teacher presenting their own Journey can edit that Journey normally;
// a teacher previewing a student's Journey sees the student's saved work
// read-only. Presentation navigation remains ephemeral either way, so moving
// around the projector never changes the saved last/visited step.
//
// The viewport takeover intentionally mirrors Lesson in a Box Present mode:
// app chrome is hidden by AITutorApp, clickers work through usePresentKeys, the
// transition is the same quiet fade, and browser fullscreen is not requested.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { guardSwanAnswer } from "../journeySwanResponseGuard";
import { isFloodRescueWholeJourneyEnabled as isWonderLabsActivityFirstEnabled } from "../floodRescueWholeJourney";
import { WONDERLABS_FLOW } from "../wonderLabsWholeJourneys";
import { startStepFromBeginning } from "../wonderLabsReviewNavigation";
import {
  Animated,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";

import JourneyPanel from "../../JourneyPanel";
import WonderLabsProgressNavigator from "./WonderLabsProgressNavigator";
import { shouldFocusSwanConversation } from "./wonderLabsWorkspaceLayout";
import { getWonderLabsDisplayDensity, configureWonderLabsSurface } from "./wonderLabsDisplayDensity";
import professorSwanImage from "../../../assets/professor_swan_transparent.png";
import { isWonderLabsLearningBeatsEnabled } from "../wonderLabsLearningBeats";
import { ChromeButton } from "../../lessonPlans/present/PresentSurface";
import PresentSarasPanel from "../../lessonPlans/present/PresentSarasPanel";
import useOpenAiChunkedSpeech from "../../lessonPlans/present/useOpenAiChunkedSpeech";
import { askSaras as presentAskSaras } from "../../lessonPlans/present/presentAskClient";
import { captureJourneyStageScreenshot } from "./journeyScreenshot";
import {
  getSwanNavigationState,
  swanActivityDomId,
  withSwanNavigation,
} from "../journeySwanActivityContext";
import {
  getPresentColors,
  getPresentType,
} from "../../lessonPlans/present/presentTheme";
import usePresentKeys from "../../lessonPlans/present/usePresentKeys";
import usePresentTransition from "../../lessonPlans/present/usePresentTransition";
import { getWonderLabsScienceGuardrail } from "../wonderLabsMediaTeaching";
import {
  isReadAloudPrimed,
  primeReadAloud,
} from "../../lessonPlans/present/useReadAloud";

// Opt-in internal comparison only. Existing guide + right-hand Ask remain the
// default. Test with ?ff_chapter_learning_path=1&ff_swan_unified_panel=1.
const unifiedSwanFlagEnabled = () => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("ff_swan_unified_panel") === "1" ||
      /^\/wonderlabs(?:\/|$)/.test(window.location.pathname);
  } catch (_error) {
    return false;
  }
};

const clampIndex = (value, total) => {
  const max = Math.max(0, total - 1);
  const numeric = Number.isFinite(Number(value)) ? Number(value) : 0;
  return Math.max(0, Math.min(max, numeric));
};

const getJourneySteps = (journey) => {
  if (Array.isArray(journey?.steps)) return journey.steps;
  if (Array.isArray(journey?.runtime?.steps)) return journey.runtime.steps;
  return [];
};

const getJourneyTitle = (journey, fallback) =>
  journey?.title ||
  journey?.name ||
  journey?.manifest?.title ||
  journey?.manifest_json?.journey?.title ||
  fallback;

const getStepPhase = (step, index, t) => {
  const translate = t || ((value) => value);
  const raw =
    step?.phase ||
    step?.phase_label ||
    step?.stage ||
    step?.section ||
    null;

  return raw ? translate(String(raw)) : `${translate("Step")} ${index + 1}`;
};


function ProfessorSwanCoachRail({
  coach,
  colors,
  type,
  readAloud,
  speaking,
  thinking = false,
  celebrating,
  onToggleReadAloud,
  onAsk,
  onShowActivity,
  collapsed,
  onToggleCollapsed,
  // Flagged inline design: Swan's current guidance stays visible while the
  // existing conversation expands underneath in the SAME rail.
  unified = false,
  conversationOpen = false,
  conversationPanel = null,
  narrowFullWidth = false,
  smallViewportExpanded = false,
  compactHeight = false,
  compactWidth = false,
  focusedConversation = false,
  allowFocusToggle = false,
  onToggleConversationFocus,
  onCloseConversation,
  t,
}) {
  const characterMotion = useRef(new Animated.Value(0)).current;
  const completionMotion = useRef(new Animated.Value(0)).current;
  const [reducedMotion, setReducedMotion] = useState(false);

  // Respect the learner's browser accessibility preference. No new assets or
  // animation dependencies: this is only a transform on our existing Swan PNG.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setReducedMotion(preference.matches);
    syncPreference();
    if (preference.addEventListener) {
      preference.addEventListener("change", syncPreference);
      return () => preference.removeEventListener("change", syncPreference);
    }
    preference.addListener?.(syncPreference);
    return () => preference.removeListener?.(syncPreference);
  }, []);

  const swanMotionState = speaking ? "speaking" : thinking ? "thinking" : "listening";
  // A slow idle float becomes a distinct thinking tilt or speaking gesture. Never
  // restart the loop just because the coaching text or student work changes.
  useEffect(() => {
    characterMotion.stopAnimation();
    characterMotion.setValue(0);
    if (reducedMotion) return undefined;

    const duration = swanMotionState === "speaking" ? 600
      : swanMotionState === "thinking" ? 1100 : 2400;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(characterMotion, {
          toValue: 1,
          duration,
          useNativeDriver: true,
        }),
        Animated.timing(characterMotion, {
          toValue: 0,
          duration,
          useNativeDriver: true,
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [swanMotionState, reducedMotion, characterMotion]);

  // One short nod when the phase reflection is ready—not on every click.
  useEffect(() => {
    if (!celebrating || reducedMotion) return undefined;
    completionMotion.setValue(0);
    const nod = Animated.sequence([
      Animated.timing(completionMotion, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(completionMotion, {
        toValue: 0,
        duration: 320,
        useNativeDriver: true,
      }),
    ]);
    nod.start();
    return () => {
      nod.stop();
      completionMotion.setValue(0);
    };
  }, [celebrating, reducedMotion, completionMotion]);

  if (!coach && !(unified && conversationOpen)) return null;
  const guide = coach || {};

  const avatarTransform = {
    transform: [
      {
        translateY: characterMotion.interpolate({
          inputRange: [0, 1],
          outputRange: [0, swanMotionState === "speaking" ? -4
            : swanMotionState === "thinking" ? -2 : -3],
        }),
      },
      {
        rotate: characterMotion.interpolate({
          inputRange: [0, 1],
          outputRange: ["0deg", swanMotionState === "speaking" ? "2deg"
            : swanMotionState === "thinking" ? "-5deg" : "-1.2deg"],
        }),
      },
      {
        scale: characterMotion.interpolate({
          inputRange: [0, 1],
          outputRange: [1, swanMotionState === "speaking" ? 1.035
            : swanMotionState === "thinking" ? 1.025 : 1.01],
        }),
      },
      {
        scale: completionMotion.interpolate({
          inputRange: [0, 1],
          outputRange: [1, 1.075],
        }),
      },
    ],
  };

  return (
    <View
      style={[
        styles.coachRail,
        // Stable desktop width: opening chat must not move the child's work.
        unified
          ? (collapsed && !conversationOpen
            ? styles.coachRailCollapsed
            : styles.coachRailConversation)
          : (collapsed ? styles.coachRailCollapsed : null),
        narrowFullWidth && smallViewportExpanded ? styles.coachRailMobileExpanded : null,
        focusedConversation ? styles.coachRailFocusedWorkspace : null,
        compactHeight && unified && conversationOpen ? { paddingVertical: 5, marginVertical: 5 } : null,
        narrowFullWidth && !smallViewportExpanded ? styles.coachRailMobileCollapsed : null,
        {
          backgroundColor: colors.surface,
          borderColor: colors.border,
        },
      ]}
    >
      <View style={styles.coachHeader}>
        <Text
          style={[
            styles.coachEyebrow,
            type.chrome,
            { color: colors.accentPrimary },
          ]}
          numberOfLines={collapsed ? 1 : 2}
        >
          {t(collapsed ? "SWAN" : "PROFESSOR SWAN")}
        </Text>
        {unified && conversationOpen ? (
          <View style={styles.coachConversationActions}>
            {allowFocusToggle ? (
              <TouchableOpacity
                onPress={onToggleConversationFocus}
                accessibilityRole="button"
                accessibilityLabel={t(focusedConversation
                  ? "Show Professor Swan beside the activity"
                  : "Expand Professor Swan conversation")}
                style={[styles.coachHideConversationButton, { borderColor: colors.border }]}
              >
                <Text style={{ color: colors.accentPrimary, fontWeight: "800", fontSize: 12 }}>
                  {t(focusedConversation ? "Side by side" : "Expand")}
                </Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              onPress={onCloseConversation}
              accessibilityRole="button"
              accessibilityLabel={t(focusedConversation
                ? "Return to learning activity"
                : "Hide Professor Swan conversation")}
              style={[styles.coachHideConversationButton, { borderColor: colors.border }]}
            >
              <Text style={{ color: colors.accentPrimary, fontWeight: "800", fontSize: 12 }}>
                {t(focusedConversation ? "Back to activity" : "Hide")} {"⌃"}
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            onPress={onToggleCollapsed}
            accessibilityRole="button"
            accessibilityLabel={t(collapsed ? "Expand Professor Swan guide" : "Collapse Professor Swan guide")}
            style={[styles.coachCollapseButton, { borderColor: colors.border }]}
          >
            <Text style={{ color: colors.accentPrimary, fontWeight: "900" }}>
              {collapsed ? "+" : "−"}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      <TouchableOpacity
        disabled={!unified || !collapsed || conversationOpen}
        onPress={onAsk}
        accessibilityRole={unified && collapsed && !conversationOpen ? "button" : undefined}
        accessibilityLabel={unified && collapsed && !conversationOpen
          ? t("Tap Professor Swan for help") : undefined}
        activeOpacity={unified && collapsed && !conversationOpen ? 0.75 : 1}
      >
        <Animated.View
          style={[
            styles.coachAvatarWrap,
            unified && conversationOpen ? styles.coachAvatarWrapConversation : null,
            focusedConversation ? styles.coachFocusedAvatarRow : null,
            compactHeight && conversationOpen ? { paddingVertical: 0 } : null,
            avatarTransform,
            speaking ? styles.coachAvatarSpeaking : null,
          ]}
        >
          <Image
            source={professorSwanImage}
            resizeMode="contain"
            accessibilityLabel={t("Professor Swan")}
            style={[
              styles.coachAvatar,
              unified && conversationOpen
                ? (focusedConversation ? { width: 34, height: 39 } : compactHeight ? { width: 40, height: 46 } : styles.coachAvatarConversation)
                : collapsed ? styles.coachAvatarCollapsed : null,
            ]}
          />
          {unified && conversationOpen ? (
            <View style={styles.coachConversationIdentity}>
              <Text style={[styles.coachConversationGreeting, { color: colors.textPrimary }]}>
                {t(guide.title || "I'm here to help!")}
              </Text>
              <Text style={{ color: colors.textMuted, fontSize: 12 }}>
                {t(speaking ? "I'm speaking…" : thinking ? "I'm thinking…" : "Let's figure it out together!")}
              </Text>
            </View>
          ) : null}
        </Animated.View>
      </TouchableOpacity>

      {unified && collapsed && !conversationOpen ? (
        <TouchableOpacity
          onPress={onAsk}
          accessibilityRole="button"
          accessibilityLabel={t("Ask Professor Swan")}
          style={[styles.coachCollapsedAskButton, { borderColor: colors.border }]}
        >
          <Text style={{ color: colors.accentPrimary, fontSize: 12, fontWeight: "900" }}>
            {t("Ask")}
          </Text>
        </TouchableOpacity>
      ) : null}

      {!conversationOpen && !collapsed && (speaking || thinking) ? (
        <View style={styles.coachSpeakingRow}>
          <Text style={{ fontSize: 14 }}>{speaking ? "🔊" : "💭"}</Text>
          <Text
            style={[
              styles.coachSpeakingText,
              { color: colors.accentPrimary },
            ]}
          >
            {t(speaking ? "Professor Swan is speaking…" : "Professor Swan is thinking…")}
          </Text>
        </View>
      ) : null}

      {!(unified && conversationOpen) && !collapsed ? (
        <Text
          style={[
            styles.coachTitle,
            { color: colors.textPrimary },
          ]}
        >
          {t(guide.title || "Keep going")}
        </Text>
      ) : null}

      {(!collapsed || (unified && conversationOpen)) && !!guide.action && !conversationOpen && (
        <ScrollView
          style={[
            styles.coachAction,
            unified && conversationOpen ? styles.coachActionWithConversation : null,
            {
              backgroundColor: colors.pageBg,
              borderColor: colors.border,
            },
          ]}
          contentContainerStyle={styles.coachActionContent}
          nestedScrollEnabled
          showsVerticalScrollIndicator
          accessibilityLabel={t("Professor Swan's guidance")}
        >
          <TouchableOpacity
            onPress={onShowActivity}
            disabled={!onShowActivity}
            accessibilityRole={onShowActivity ? "button" : undefined}
            accessibilityLabel={onShowActivity ? t("Show me where to work") : undefined}
            activeOpacity={0.8}
            style={{ paddingVertical: 5 }}
          >
            <Text style={[styles.coachActionLabel, { color: colors.accentPrimary }]}>
              {t(guide.actionLabel || "DO THIS NOW")}
            </Text>
            <Text style={[styles.coachActionText, { color: colors.textPrimary }]}>
              {t(guide.action)}
            </Text>
            {onShowActivity ? (
              <Text style={{ marginTop: 8, color: colors.accentPrimary, fontSize: 13, fontWeight: "800" }}>
                {t("Show me where ↘")}
              </Text>
            ) : null}
          </TouchableOpacity>
        </ScrollView>
      )}

      {!conversationOpen && !collapsed ? <View style={styles.coachButtons}>
        <TouchableOpacity
          onPress={onAsk}
          accessibilityRole="button"
          style={[
            styles.coachButton,
            { borderColor: colors.border },
          ]}
        >
          <Text style={{ color: colors.textPrimary, fontWeight: "800" }}>
            {t("Ask Professor Swan")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={onToggleReadAloud}
          accessibilityRole="button"
          style={[
            styles.coachButton,
            { borderColor: colors.border },
          ]}
        >
          <Text style={{ color: colors.textPrimary, fontWeight: "800" }}>
            {t(readAloud ? "Read aloud: On" : "Read aloud: Off")}
          </Text>
        </TouchableOpacity>
      </View> : null}
      {unified ? (
        <View
          style={[
            styles.coachEmbeddedConversation,
            conversationOpen ? styles.coachEmbeddedConversationVisible : null,
            { display: conversationOpen ? "flex" : "none", borderTopColor: colors.border },
          ]}
          accessibilityLabel={t("Ask Professor Swan conversation")}
        >
          {conversationPanel}
        </View>
      ) : null}
    </View>
  );
}

export default function JourneyPresentMode({
  journey,
  displayTitle = null,
  stepIndex = 0,
  visitedThroughIndex = 0,
  onStepChange,
  authToken,
  userId,
  localLanguage = "en",
  lang,
  isDarkMode = false,
  handleVideoClick,
  handlePWOClick,
  sessionState,
  activityCursorRef,
  onActivityCursorChange,
  onSessionStateChange,
  readOnly = false,
  presentationLabel = null,
  experienceMode = "teacher_present",
  swanPanelPreference,
  onSwanPanelPreferenceChange,
  swanReadAloudPreference,
  onSwanReadAloudPreferenceChange,
  onContextChange,
  onMissionHome,
  onExit,
  exitLabel = null,
  t = (value) => value,
}) {
  const { width, height } = useWindowDimensions();
  const isSelfGuidedFocus = experienceMode === "self_guided_focus";
  // Sample screen metrics only once. React to viewport reflow for accessibility,
  // but never recompute density when the learner presses Ctrl+ / Ctrl-.
  const displayDensity = useMemo(
    () => isSelfGuidedFocus ? getWonderLabsDisplayDensity() : 1,
    [isSelfGuidedFocus]
  );
  useEffect(() => {
    if (!isSelfGuidedFocus || typeof document === "undefined") return undefined;
    const node = document.getElementById("wonderlabs-density-surface");
    return configureWonderLabsSurface(node, displayDensity);
  }, [isSelfGuidedFocus, displayDensity]);
  // Virtual layout dimensions reflect the additional space provided by the
  // application density profile, not a change in browser zoom.
  const layoutWidth = width / displayDensity;
  const layoutHeight = height / displayDensity;

  // Evaluate the opt-in desktop layout before keyboard/navigation callbacks.
  const allStepsForPilot = getJourneySteps(journey);
  const selectedStepForPilot = allStepsForPilot[clampIndex(stepIndex, allStepsForPilot.length)];
  const missionActivityPilot = isSelfGuidedFocus && isWonderLabsActivityFirstEnabled() &&
    Boolean(WONDERLABS_FLOW[String(selectedStepForPilot?.step_id || selectedStepForPilot?.id || "")]);
  // V2.6: The opt-in WonderLabs experience uses the SAME contextual Swan
  // conversation at every viewport width. The old right-side overlay belongs
  // only to unflagged/legacy journeys, never to the five activity-first missions.
  const swanFlagEnabled = unifiedSwanFlagEnabled();
  const showCoachRail = isSelfGuidedFocus &&
    (width >= 1180 || (missionActivityPilot && swanFlagEnabled));
  const unifiedSwanPanel = showCoachRail && swanFlagEnabled;
  const colors = useMemo(() => getPresentColors(isDarkMode), [isDarkMode]);
  const type = useMemo(() => getPresentType(width, height), [width, height]);
  const steps = useMemo(() => getJourneySteps(journey), [journey]);
  const total = steps.length;

  const index = clampIndex(stepIndex, total);
  const atEnd = total <= 0 || index >= total - 1;
  const activeStep = total > 0 ? steps[index] : null;
  const opacity = usePresentTransition(index);

  // JourneyPageRenderer already builds the same hidden-context/student-work
  // payload used by the normal Saras rail. Presentation keeps that context but
  // sends the ask through Zach's presentation-only client, so the answer lands
  // on the projector rather than in the signed-in user's normal Saras history UI.
  const [activeJourneyContext, setActiveJourneyContext] = useState(null);
  const [pageCompletion, setPageCompletion] = useState(null);
  const activityNavRef = useRef(null);
  const [activityNavState, setActivityNavState] = useState(null);
  // Register the child renderer's activity controls with the existing footer.
  // Ref holds callbacks; state holds only primitives so it cannot trigger an
  // endless child->parent rerender loop.
  const registerActivityNavigation = useCallback((next) => {
    activityNavRef.current = next;
    const snapshot = next ? { stepId: next.stepId, index: next.index,
      total: next.total, title: next.title, canAdvance: next.canAdvance,
      missingLabel: next.missingLabel || "", beatPending: Boolean(next.beatPending),
      beatIndex: next.beatIndex || 0, nextLabel: next.nextLabel || "",
      nextTitle: next.nextTitle || "", previousLabel: next.previousLabel || "" } : null;
    setActivityNavState((previous) => {
      if (!snapshot && !previous) return previous;
      if (snapshot && previous && Object.keys(snapshot).every((key) =>
        snapshot[key] === previous[key])) return previous;
      return snapshot;
    });
  }, []);
  const [continueNotice, setContinueNotice] = useState("");
  useEffect(() => {
    setContinueNotice((current) => current === "Opening the next activity…" ? "" : current);
  }, [activityNavState?.stepId, activityNavState?.index]);
  const [localReadAloudEnabled, setLocalReadAloudEnabled] = useState(true);
  // Keep the learner's existing preference (normally On). V2 suppresses
  // unsolicited narration, but Swan's requested answers should speak aloud.
  const swanReadAloudEnabled = typeof swanReadAloudPreference === "boolean"
    ? swanReadAloudPreference : localReadAloudEnabled;
  const setSwanReadAloudEnabled = (update) => {
    const next = typeof update === "function" ? update(swanReadAloudEnabled) : update;
    if (typeof swanReadAloudPreference !== "boolean") setLocalReadAloudEnabled(next);
    onSwanReadAloudPreferenceChange?.(next);
  };
  // Keep the child-reported coach state across the phase-entry effect.
  // The renderer reports its initial state in an effect, which can run before
  // this parent's [index] cleanup effect on the same mount. Clearing it there
  // loses the first cue until a student edits an answer.
  const [reportedCoachState, setCoachState] = useState(null);
  const [coachAnnouncement, setCoachAnnouncement] = useState("");
  const [pendingTransition, setPendingTransition] = useState(null);
  // A phase review is shown in the same visible Swan conversation as a normal
  // question. It is not an invisible voice-only transition.
  const [phaseReviewAnswer, setPhaseReviewAnswer] = useState(null);
  // A child can reclaim activity space without losing spoken coaching.
  const persistSwanInR2 = isSelfGuidedFocus && isWonderLabsLearningBeatsEnabled();
  const [swanGuideCollapsed, setSwanGuideCollapsed] = useState(() =>
    persistSwanInR2 && typeof swanPanelPreference?.collapsed === "boolean"
      ? swanPanelPreference.collapsed : missionActivityPilot);
  const [journeyMapOpen, setJourneyMapOpen] = useState(false);
  const [speechPrimed, setSpeechPrimed] = useState(() => isReadAloudPrimed());
  const [aiPanelSpeaking, setAiPanelSpeaking] = useState(false);
  const [aiPanelThinking, setAiPanelThinking] = useState(false);
  const [sarasOpen, setSarasOpen] = useState(() =>
    persistSwanInR2 && Boolean(swanPanelPreference?.conversationOpen));
  // No cleanup writes: a media viewer replacing this component must never
  // overwrite a child's last explicit Swan state with the component default.
  useEffect(() => {
    if (!persistSwanInR2) return;
    onSwanPanelPreferenceChange?.((previous) => {
      if (previous?.collapsed === swanGuideCollapsed &&
          previous?.conversationOpen === sarasOpen) return previous;
      return { collapsed: swanGuideCollapsed, conversationOpen: sarasOpen };
    });
  }, [persistSwanInR2, swanGuideCollapsed, sarasOpen, onSwanPanelPreferenceChange]);
  // A desktop learner can keep the investigation visible while asking Swan.
  // Focused reading is an explicit choice on desktop; only genuinely narrow
  // mobile viewports take over the stage automatically. Height alone must not
  // trigger a takeover at 1080p / Windows 150% display scaling.
  const [swanFocusRequested, setSwanFocusRequested] = useState(false);
  const compactWonderLabsViewport = isSelfGuidedFocus && (layoutWidth < 1450 || layoutHeight < 900);
  const focusedSwanConversation = shouldFocusSwanConversation({
    width,
    height,
    unified: unifiedSwanPanel,
    open: sarasOpen,
    expanded: swanFocusRequested,
  });
  const expandedNarrowCoach = unifiedSwanPanel && width < 760 &&
    (!swanGuideCollapsed || sarasOpen);
  const swanOccupiesSmallViewport = focusedSwanConversation || expandedNarrowCoach;
  const swanSpeech = useOpenAiChunkedSpeech({ userId, language: localLanguage || lang || "en" });
  const [sarasQuestion, setSarasQuestion] = useState(null);
  const [sarasContext, setSarasContext] = useState(null);
  const narratedCoachKeyRef = useRef("");
  const initialWelcomeDoneRef = useRef(false);
  const pendingActionRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const reflectionRequestRef = useRef(null);
  // The embedded conversation survives a return to the guide in the same
  // phase; its internal question/answer thread is reset on the next phase.
  const swanConversationOpenedRef = useRef(false);

  // The same preference controls dynamic OpenAI narration and streamed
  // conversation. Stop pending cloud synthesis and queued playback on Off.
  useEffect(() => {
    if (!swanReadAloudEnabled) swanSpeech.stop();
  }, [swanReadAloudEnabled, swanSpeech.stop]);

  const needsSpeechStart =
    isSelfGuidedFocus &&
    swanReadAloudEnabled &&
    swanSpeech.supported &&
    !speechPrimed;

  const startProfessorSwan = useCallback(() => {
    const primed = primeReadAloud(localLanguage || lang || "en");
    setSpeechPrimed(Boolean(primed || isReadAloudPrimed()));
  }, [lang, localLanguage]);

  const activeStepId = String(activeStep?.step_id || activeStep?.id || "");
  // Never display or narrate a report belonging to the previous phase while
  // the newly mounted child is computing the current phase's guidance.
  const coachState =
    reportedCoachState?.stepId === activeStepId
      ? reportedCoachState
      : null;
  const fallbackJourneyContext = useMemo(
    () => ({
      journey_id: journey?.journey_id ?? journey?.id ?? null,
      journey_slug:
        journey?.slug ??
        journey?.manifest?.slug ??
        journey?.manifest_json?.journey?.slug ??
        null,
      step_id: activeStepId || null,
      step_title: activeStep?.title || activeStep?.name || null,
      student_work:
        activeStepId && sessionState?.steps?.[activeStepId]
          ? sessionState.steps[activeStepId]
          : {},
      suggested_questions: [],
      active_activity: { type: "question" },
    }),
    [activeStep, activeStepId, journey, sessionState?.steps]
  );

  const activeContextMatchesStep =
    !!activeStepId &&
    String(activeJourneyContext?.step_id || "") === activeStepId;
  const currentJourneyContext = activeContextMatchesStep
    ? activeJourneyContext
    : fallbackJourneyContext;
  // Speak a critical physical instruction ONCE on entering an opted-in beat.
  // Respect the child's read-aloud preference, the browser's audio gesture,
  // and the existing Swan voice engine; never start a second speech source.
  const spokenBriefingsRef = useRef(new Set());
  const physicalBriefing = currentJourneyContext?.activity_progress?.physical_briefing;
  const mediaBriefing = currentJourneyContext?.activity_progress?.media_briefing;
  // One spoken invitation per part, never a separate voice for every media tile.
  // Physical safety/action guidance takes priority if a beat includes both.
  const activeBriefing = physicalBriefing?.spoken ? physicalBriefing : mediaBriefing;
  const activeBriefingKey = activeBriefing?.spoken
    ? `${activeStepId}:${currentJourneyContext?.activity_progress?.focused_activity?.id || ""}:${currentJourneyContext?.activity_progress?.focused_activity?.learning_beat?.number || 0}:${activeBriefing.spoken}`
    : "";
  useEffect(() => {
    if (!missionActivityPilot || !swanReadAloudEnabled || !speechPrimed ||
        aiPanelSpeaking || aiPanelThinking || Boolean(coachAnnouncement) ||
        !swanSpeech.supported || swanSpeech.speaking || !activeBriefingKey ||
        spokenBriefingsRef.current.has(activeBriefingKey)) return;
    spokenBriefingsRef.current.add(activeBriefingKey);
    swanSpeech.speak(activeBriefing.spoken);
  }, [missionActivityPilot, swanReadAloudEnabled, speechPrimed,
      aiPanelSpeaking, aiPanelThinking, coachAnnouncement,
      swanSpeech.supported, swanSpeech.speaking, swanSpeech.speak,
      activeBriefingKey, activeBriefing?.spoken]);

  const readMasterTeacherInstructions = useCallback((words) => {
    if (!String(words || "").trim()) return;
    startProfessorSwan(); // User gesture primes audio when needed.
    if (activeBriefing?.spoken === words && activeBriefingKey) {
      spokenBriefingsRef.current.add(activeBriefingKey);
    }
    swanSpeech.stop();
    if (swanSpeech.supported) swanSpeech.speak(String(words));
  }, [startProfessorSwan, swanSpeech.speak, swanSpeech.stop,
    swanSpeech.supported, activeBriefing?.spoken, activeBriefingKey]);

  // Feed the existing asset Ask pipeline with the same up-to-date saved work
  // and activity context that the in-Journey Swan panel receives. No new store.
  useEffect(() => {
    if (isSelfGuidedFocus) onContextChange?.(currentJourneyContext);
  }, [isSelfGuidedFocus, currentJourneyContext, onContextChange]);

  // Hide stale coaching only when the actual next task changes (for example,
  // a corrected selection advances to writing a theory). Typing in the same
  // response field does not collapse chat. The previous exchange is retained
  // in the mounted conversation and is available if the child reopens it.
  const nextTaskIdentity = [
    activeStepId,
    currentJourneyContext?.activity_progress?.next_action?.block_id || "phase-complete",
    currentJourneyContext?.activity_progress?.phase_complete ? "complete" : "working",
  ].join(":");
  const lastTaskIdentityRef = useRef(null);
  useEffect(() => {
    const previous = lastTaskIdentityRef.current;
    lastTaskIdentityRef.current = nextTaskIdentity;
    if (!isSelfGuidedFocus || !unifiedSwanPanel || !previous ||
        previous === nextTaskIdentity ||
        !currentJourneyContext?.activity_progress) return;
    // The DO THIS NOW cue remains visible. We do not send another AI request.
    // R2: changing focus to a video/PWO must not silently close a learner's
    // chosen conversation. Its content is already scoped by the activity key.
    if (!persistSwanInR2) setSarasOpen(false);
    setAiPanelSpeaking(false);
  }, [nextTaskIdentity, isSelfGuidedFocus, unifiedSwanPanel,
      persistSwanInR2, currentJourneyContext?.activity_progress]);

  const sarasLanguage = localLanguage || "en";
  // Match the actual footer button throughout review/advance transitions.
  // Derived from existing completion + transition state; never persisted.
  const navigation = useMemo(() => getSwanNavigationState({
    isSelfGuidedFocus, atEnd, pageCompletion, pendingTransition, index,
  }), [isSelfGuidedFocus, atEnd, pageCompletion, pendingTransition, index]);

  const hasAnySavedWork = useMemo(() => {
    const stepsState =
      sessionState?.steps && typeof sessionState.steps === "object"
        ? sessionState.steps
        : {};

    return Object.values(stepsState).some((stepState) => {
      if (!stepState || typeof stepState !== "object") return false;

      return Object.values(stepState).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === "object") {
          return Object.keys(value).length > 0;
        }
        return String(value ?? "").trim().length > 0;
      });
    });
  }, [sessionState?.steps]);


  // First entry into self-guided focus always gets one teacher-like orientation.
  // If the current phase is already complete, say so rather than replaying its
  // coaching. Later visits to completed phases stay quiet.
  useEffect(() => {
    if (!isSelfGuidedFocus || missionActivityPilot || !coachState || initialWelcomeDoneRef.current) return;
    if (pageCompletion === null) return;
    if (swanReadAloudEnabled && swanSpeech.supported && !speechPrimed) return;

    initialWelcomeDoneRef.current = true;

    let opening = "";
    if (hasAnySavedWork) {
      opening = coachState.complete
        ? `Welcome back. You are on Phase ${index + 1}, and this part is already complete. You can review it or continue when you are ready.`
        : `Welcome back. ${coachState.action || "Let’s continue where you stopped."}`;
    } else {
      // A teacher-like mission opening has two jobs: welcome the learner, then
      // give the first concrete action. Authored `welcome` copy should not hide
      // the setup/action cue that follows it.
      opening = [
        coachState.welcome || "Welcome, Future Engineer!",
        coachState.action,
      ]
        .filter(Boolean)
        .join(" ");
    }

    if (!String(opening || "").trim()) return;

    setCoachAnnouncement(opening);

    if (swanReadAloudEnabled && swanSpeech.supported) {
      swanSpeech.speak(opening, {
        onEnd: () => setCoachAnnouncement(""),
        onError: () => setCoachAnnouncement(""),
      });
    } else {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
      transitionTimerRef.current = setTimeout(() => {
        setCoachAnnouncement("");
      }, 5000);
    }

    narratedCoachKeyRef.current = `${index}:${coachState.action || ""}`;
  }, [
    coachState,
    hasAnySavedWork,
    index,
    isSelfGuidedFocus,
    missionActivityPilot,
    pageCompletion,
    speechPrimed,
    swanReadAloudEnabled,
    swanSpeech.speak,
    swanSpeech.supported,
  ]);

  // When required work advances inside an unfinished phase, queue the newest
  // action. Never interrupt a welcome/transition that is already speaking.
  useEffect(() => {
    if (!isSelfGuidedFocus || missionActivityPilot || !coachState?.action) return;
    if (coachState.complete) return;
    if (!initialWelcomeDoneRef.current) return;

    const key = `${index}:${coachState.action}`;
    if (narratedCoachKeyRef.current === key) return;

    if (
      coachAnnouncement ||
      (swanReadAloudEnabled && swanSpeech.speaking)
    ) {
      pendingActionRef.current = {
        key,
        text: coachState.action,
      };
      return;
    }

    narratedCoachKeyRef.current = key;

    if (swanReadAloudEnabled && swanSpeech.supported) {
      swanSpeech.speak(coachState.action);
    }
  }, [
    coachAnnouncement,
    coachState?.action,
    coachState?.complete,
    index,
    isSelfGuidedFocus,
    missionActivityPilot,
    swanReadAloudEnabled,
    swanSpeech.speak,
    swanSpeech.speaking,
    swanSpeech.supported,
  ]);

  // Once a higher-priority message ends, speak only the latest queued action.
  useEffect(() => {
    if (!isSelfGuidedFocus || missionActivityPilot) return;
    if (coachAnnouncement) return;
    if (swanSpeech.speaking) return;

    const pending = pendingActionRef.current;
    if (!pending) return;

    pendingActionRef.current = null;
    if (narratedCoachKeyRef.current === pending.key) return;

    narratedCoachKeyRef.current = pending.key;
    if (swanReadAloudEnabled && swanSpeech.supported) {
      swanSpeech.speak(pending.text);
    }
  }, [
    coachAnnouncement,
    isSelfGuidedFocus,
    missionActivityPilot,
    swanReadAloudEnabled,
    swanSpeech.speak,
    swanSpeech.speaking,
    swanSpeech.supported,
  ]);

  useEffect(
    () => () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
      reflectionRequestRef.current?.abort();
    },
    []
  );

  // A completed phase may be left or the guide closed while AI feedback is
  // still pending. Cancel that request so its answer cannot appear on a new
  // phase, speak out of order, or overwrite a newer reflection.
  useEffect(() => () => {
    reflectionRequestRef.current?.abort();
    reflectionRequestRef.current = null;
  }, [index]);

  useEffect(() => {
    if (missionActivityPilot) swanSpeech.stop();
  }, [missionActivityPilot, swanSpeech.stop]);

  const closeSaras = useCallback(() => {
    setSwanFocusRequested(false);
    setSarasOpen(false);
    setSarasQuestion(null);
    setSarasContext(null);
    setAiPanelSpeaking(false);
  }, []);

  const toggleSaras = useCallback(() => {
    swanSpeech.stop();
    setSarasOpen((open) => {
      if (open) {
        setSarasQuestion(null);
        setSarasContext(null);
        return false;
      }
      setSarasQuestion(null);
      setSarasContext(currentJourneyContext);
      return true;
    });
  }, [currentJourneyContext, swanSpeech.stop]);

  const openProfessorSwanHelp = useCallback(
    (contextPayload = null) => {
      // Opening from the child's tap can unlock browser audio; do it in the
      // user gesture rather than expecting a later network response to do so.
      if (missionActivityPilot && swanReadAloudEnabled) startProfessorSwan();
      swanSpeech.stop();
      setSarasQuestion(null);
      setPhaseReviewAnswer(null);
      setSarasContext(contextPayload || currentJourneyContext);
      setSwanGuideCollapsed(false);
      setSarasOpen(true);
    },
    [currentJourneyContext, missionActivityPilot, startProfessorSwan, swanReadAloudEnabled, swanSpeech.stop]
  );

  const askQuestionFromJourney = useCallback(
    (question, contextPayload = null) => {
      const text = String(question || "").trim();
      if (!text) return;
      if (missionActivityPilot && swanReadAloudEnabled) startProfessorSwan();
      swanSpeech.stop();
      setSarasQuestion(text);
      setPhaseReviewAnswer(null);
      setSarasContext(contextPayload || currentJourneyContext);
      setSwanGuideCollapsed(false);
      setSarasOpen(true);
    },
    [currentJourneyContext, missionActivityPilot, startProfessorSwan, swanReadAloudEnabled, swanSpeech.stop]
  );

  const askSarasWithJourneyContext = useCallback(
    async (options) => {
      // The overlay stays open while students revise their answers. Reuse the
      // current saved work for every new question rather than the frozen work
      // from the moment the panel opened.
      const captured =
        sarasContext?.step_id === currentJourneyContext?.step_id
          ? sarasContext
          : null;
      const initialChecklistHelp =
        Boolean(options?.newDialog) &&
        captured?.active_activity?.type === "build_checklist";
      const mergedContext = {
        ...(captured || {}),
        ...currentJourneyContext,
        ...(getWonderLabsScienceGuardrail(activeStepId)
          ? { scientific_constraints: getWonderLabsScienceGuardrail(activeStepId) }
          : {}),
        student_work: {
          ...(captured?.student_work || {}),
          ...(currentJourneyContext?.student_work || {}),
          // A checklist click fires the question on the same event that saves
          // the help status; React state may not have flushed yet. For that
          // FIRST question only, use the just-clicked item and its status.
          ...(initialChecklistHelp ? captured.student_work || {} : {}),
        },
        active_activity: initialChecklistHelp
          ? captured.active_activity
          : currentJourneyContext?.active_activity || { type: "question" },
      };
      if (isSelfGuidedFocus && mergedContext.activity_progress) {
        const focusedNav = activityNavRef.current;
        const currentNavigation = missionActivityPilot && focusedNav?.stepId === activeStepId &&
          focusedNav.index < focusedNav.total - 1
          ? { label: focusedNav.canAdvance ? "Next activity" : "Complete this activity",
              disabled: !focusedNav.canAdvance, stage: "activity" }
          : navigation;
        mergedContext.activity_progress = withSwanNavigation(
          mergedContext.activity_progress, currentNavigation
        );
      }
      // Capture only the visible Journey stage when a child asks in
      // self-guided mode. Never delay or block a question if capture fails.
      const screenshot = isSelfGuidedFocus
        ? await captureJourneyStageScreenshot()
        : null;
      const guard = isSelfGuidedFocus
        ? mergedContext.activity_progress?.inquiry_guard
        : null;
      const safeDelta = guard?.protect_answer ? undefined : options?.onDelta;
      const result = await presentAskSaras({
        ...options,
        onDelta: safeDelta,
        // Trust the current merged inquiry guard, not a possibly stale panel render.
        audioEnabled: Boolean(options?.audioEnabled && !guard?.protect_answer),
        onAudioChunk: guard?.protect_answer ? undefined : options?.onAudioChunk,
        screenshot,
        journeyContext: mergedContext,
      });
      return result == null ? result : guardSwanAnswer(result, guard, options?.question, {
        hintAttempt: options?.hintAttempt,
        previousAnswer: options?.previousAnswer,
      });
    },
    [currentJourneyContext, sarasContext, isSelfGuidedFocus, navigation,
      missionActivityPilot, activeStepId]
  );

  // A Saras answer belongs to the phase that supplied its hidden context and
  // student work. Moving the presentation closes it rather than carrying a
  // stale answer onto the next phase.
  useEffect(() => {
    setSwanFocusRequested(false);
    if (!persistSwanInR2) setSarasOpen(false);
    setSarasQuestion(null);
    setSarasContext(null);
    setPhaseReviewAnswer(null);
    setContinueNotice("");
    // Do not clear coach state here: the child can have reported the new
    // phase before this effect runs. The stepId guard above prevents stale
    // guidance during phase changes.
    setPendingTransition(null);
    pendingActionRef.current = null;
  }, [index, persistSwanInR2]);

  const goTo = useCallback(
    (next, details) => {
      if (total <= 0 || !onStepChange) return;
      reflectionRequestRef.current?.abort();
      reflectionRequestRef.current = null;
      swanSpeech.stop();
      // Clear before the new child renderer mounts. That renderer will then
      // report the actual saved completion state for the destination step.
      setPageCompletion(null);
      setPhaseReviewAnswer(null);
      const targetIndex = clampIndex(next, total);
      // A manual phase revisit, or sequential Next into a phase already seen,
      // opens the first activity and first part. This is a display cursor only.
      if (missionActivityPilot && (details?.intent === "manual" ||
          (details?.intent !== "evidence-recovery" && targetIndex > index))) {
        const target = steps[targetIndex];
        startStepFromBeginning(activityCursorRef?.current,
          String(target?.step_id || target?.id || ""));
      }
      onStepChange(targetIndex, details);
    },
    [onStepChange, total, swanSpeech.stop, missionActivityPilot,
      index, steps, activityCursorRef]
  );

  const goPrevious = useCallback(() => {
    const nav = activityNavRef.current;
    if (missionActivityPilot && nav?.stepId === activeStepId &&
        (nav.index > 0 || nav.beatIndex > 0)) {
      setPendingTransition(null);
      setCoachAnnouncement("");
      swanSpeech.stop();
      nav.retreat?.();
      return;
    }
    goTo(index - 1);
  }, [goTo, index, missionActivityPilot, activeStepId, swanSpeech.stop]);
  const lastNextPressRef = useRef(0);
  const goNext = useCallback(() => {
    // A double-tap while React is committing the new child activity used to
    // invoke the stale advance handler twice. Give each visible transition
    // one stable interaction window, without changing the evidence gates.
    if (missionActivityPilot) {
      const now = Date.now();
      if (now - lastNextPressRef.current < 450) return;
      lastNextPressRef.current = now;
    }
    const activity = activityNavRef.current;
    if (missionActivityPilot && activity?.stepId === activeStepId &&
        (activity.index < activity.total - 1 || activity.beatPending)) {
      if (activity.canAdvance) {
        setContinueNotice(activity.beatPending ? "" : "Opening the next activity…");
        activity.advance?.();
      } else {
        setContinueNotice(activity.missingLabel
          ? `To continue: ${activity.missingLabel}`
          : `Complete “${activity.title}” before continuing.`);
        activity.revealMissing?.();
      }
      return;
    }
    if (missionActivityPilot && pageCompletion?.hasGate && !pageCompletion.allComplete) {
      const required = pageCompletion.firstIncomplete?.label;
      setContinueNotice(required ? `To finish this step: ${required}` :
        "Finish the required investigation evidence before moving on.");
      activity?.revealMissing?.();
      return;
    }
    if (isSelfGuidedFocus && index < total - 1) {
      const incomplete =
        pageCompletion?.hasGate && pageCompletion?.allComplete === false;

      if (incomplete) {
        const nextJob =
          pageCompletion?.firstIncomplete?.label ||
          "finish the highlighted activity";
        setContinueNotice(`Almost there — ${nextJob} first.`);
        return;
      }

      if (pageCompletion === null) {
        setContinueNotice("One moment — Professor Swan is checking this step.");
        return;
      }
    }

    setContinueNotice("");

    // Keep the evidence-aware recap as a deliberate teacher moment, but give
    // each footer action one explicit purpose: "Review this phase" followed by
    // "Next phase". Never erase a reflection by remounting the next phase.
    if (isSelfGuidedFocus && pendingTransition?.fromIndex === index) {
      // Do not let a second click bypass the AI reflection while it is loading.
      if (pendingTransition.status === "loading") return;
      setPendingTransition(null);
      setCoachAnnouncement("");
      pendingActionRef.current = null;
      swanSpeech.stop();

      // On the final phase, the second press should actually close the guide.
      // `goTo(index + 1)` clamps back to the same last index, which previously
      // made "End of Journey" a dead end.
      if (index >= total - 1) {
        onExit?.();
      } else {
        goTo(index + 1);
      }
      return;
    }

    if (isSelfGuidedFocus && coachState) {
      // Phase-end teaching takes priority over an earlier Ask conversation.
      // The embedded conversation remains mounted and can be reopened.
      if (unifiedSwanPanel) {
        // The child asked for a phase review: show the rail immediately.
        // The response will appear in its embedded conversation, not just TTS.
        setSwanGuideCollapsed(false);
        // Make the text surface visible at the instant Review is requested,
        // including in narrow/browser-agent viewports. The pending guidance
        // announces progress and the completed recap replaces it here.
        setSarasOpen(true);
        if (missionActivityPilot && swanReadAloudEnabled) startProfessorSwan();
      }
      setPhaseReviewAnswer(null);
      const transitionText = [
        coachState.transitionSummary,
        coachState.transitionEvidence,
        coachState.nextPreview,
      ]
        .filter(Boolean)
        .join(" ");

      if (transitionText) {
        pendingActionRef.current = null;
        if (transitionTimerRef.current) {
          clearTimeout(transitionTimerRef.current);
          transitionTimerRef.current = null;
        }

        // Most phase gates verify an action (opening evidence, checking a part,
        // visiting a 3D guide), not a recorded scientific result. Such phases
        // need only the authored teacher recap, without a costly AI request.
        // When the learner actually saved answers or measurements, let Swan
        // INTERPRET that evidence instead of reading it back verbatim.
        const canReflect = Boolean(
          coachState.transitionEvidence &&
          activeContextMatchesStep &&
          currentJourneyContext?.student_work &&
          !readOnly
        );
        if (canReflect) {
          const controller = new AbortController();
          reflectionRequestRef.current?.abort();
          reflectionRequestRef.current = controller;
          setPendingTransition({ fromIndex: index, status: "loading" });
          setCoachAnnouncement("Professor Swan is considering what you recorded…");
          const reflectionContext = {
            ...currentJourneyContext,
            response_purpose: "phase_reflection",
            completed_phase_summary: coachState.transitionSummary,
            completed_phase_evidence: coachState.transitionEvidence,
            next_phase_preview: coachState.nextPreview,
            // Current phase and student work must take priority over older
            // questions still present in the conversational model history.
          };
          const question =
            "Professor Swan, briefly review my actual saved work for the " +
            "phase I just completed. Help me learn from it, then introduce " +
            "the next phase. Do not merely repeat my answers.";

          presentAskSaras({
            question,
            userId,
            language: sarasLanguage,
            journeyContext: reflectionContext,
            signal: controller.signal,
          })
            .then((feedback) => {
              if (controller.signal.aborted) return;
              const spoken = String(feedback || "").trim() ||
                [coachState.transitionSummary, coachState.nextPreview]
                  .filter(Boolean).join(" ");
              setCoachAnnouncement(spoken);
              setPendingTransition({ fromIndex: index, status: "ready", text: spoken });
              if (unifiedSwanPanel && missionActivityPilot) {
                setPhaseReviewAnswer({ fromIndex: index, text: spoken });
                setSarasOpen(true);
              } else if (swanReadAloudEnabled && swanSpeech.supported) {
                swanSpeech.speak(spoken);
              }
            })
            .catch((error) => {
              if (controller.signal.aborted) return;
              console.warn("Professor Swan phase reflection unavailable:", error);
              // Authored, evidence-accurate fallback. Never read out raw
              // student text as though quoting it were teacher feedback.
              const fallback = [
                coachState.transitionSummary,
                coachState.nextPreview,
              ].filter(Boolean).join(" ");
              setCoachAnnouncement(fallback);
              setPendingTransition({ fromIndex: index, status: "ready", text: fallback });
              if (unifiedSwanPanel && missionActivityPilot) {
                setPhaseReviewAnswer({ fromIndex: index, text: fallback });
                setSarasOpen(true);
              } else if (swanReadAloudEnabled && swanSpeech.supported) {
                swanSpeech.speak(fallback);
              }
            })
            .finally(() => {
              if (reflectionRequestRef.current === controller) {
                reflectionRequestRef.current = null;
              }
            });
          return;
        }

        // No authored learner evidence (or not enough context to safely ask):
        // rely on the tested phase/gate-based recap. No invented results.
        const fallback = [coachState.transitionSummary, coachState.nextPreview]
          .filter(Boolean).join(" ");
        setCoachAnnouncement(fallback);
        setPendingTransition({ fromIndex: index, status: "ready", text: fallback });
        if (unifiedSwanPanel && missionActivityPilot) {
          setPhaseReviewAnswer({ fromIndex: index, text: fallback });
          setSarasOpen(true);
        } else if (swanReadAloudEnabled && swanSpeech.supported) {
          swanSpeech.speak(fallback);
        }
        return;
      }
    }

    goTo(index + 1);
  }, [
    coachState,
    goTo,
    index,
    missionActivityPilot,
    activeStepId,
    isSelfGuidedFocus,
    pageCompletion,
    pendingTransition,
    onExit,
    activeContextMatchesStep,
    currentJourneyContext,
    readOnly,
    userId,
    sarasLanguage,
    swanReadAloudEnabled,
    swanSpeech.speak,
    swanSpeech.stop,
    swanSpeech.supported,
    total,
    unifiedSwanPanel,
    startProfessorSwan,
  ]);

  // Match Lesson Present mode: Space/Enter dismisses Saras first instead of
  // changing the phase behind an answer the room is still reading. Arrow/clicker
  // navigation may still move phases, and the phase effect above closes Saras.
  const advance = useCallback(() => {
    if (sarasOpen && !unifiedSwanPanel) {
      closeSaras();
      return;
    }
    goNext();
  }, [closeSaras, goNext, sarasOpen, unifiedSwanPanel]);

  usePresentKeys({
    onPrevious: goPrevious,
    onNext: goNext,
    onAdvance: advance,
    onExit,
    onToggleSaras: total > 0 ? toggleSaras : undefined,
    onCloseSaras: sarasOpen ? closeSaras : undefined,
    enabled: total > 0,
  });

  // The catalog-facing title may omit an age suffix; keep the runtime title
  // and all persisted Journey identifiers unchanged.
  const title = isSelfGuidedFocus && displayTitle
    ? displayTitle : getJourneyTitle(journey, t("Learning Journey"));
  const phase = getStepPhase(activeStep, index, t);
  const atStart = index <= 0;
  const progress = total > 0 ? Math.round(((index + 1) / total) * 100) : 0;
  const showPhaseDots = isSelfGuidedFocus && total > 1 && total <= 12 && width >= 1280;
  const targetActivityId = isSelfGuidedFocus &&
    pendingTransition?.fromIndex !== index
      ? currentJourneyContext?.activity_progress?.next_action?.block_id
      : null;
  const showCurrentActivity = targetActivityId ? () => {
    // DOM anchors live on existing authored block shells. Do not change any
    // ScrollView state, progress, or focus; just locate the visible card.
    if (typeof document === "undefined") return;
    const stage = document.getElementById("swan-journey-stage");
    const element = document.getElementById(swanActivityDomId(targetActivityId));
    if (!element || !stage?.contains(element)) return;
    element.scrollIntoView?.({ behavior: "smooth", block: "center" });
    if (typeof element.animate === "function" &&
        !(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches)) {
      element.animate([
        { outline: "3px solid transparent", outlineOffset: "2px" },
        { outline: "3px solid #0f766e", outlineOffset: "2px" },
        { outline: "3px solid transparent", outlineOffset: "2px" },
      ], { duration: 1300, iterations: 1 });
    }
  } : null;
  // The footer and DO THIS NOW must name the same action, including after a
  // completed phase changes the button from Continue to Review this phase.
  const completedPhaseCoachAction = isSelfGuidedFocus && pageCompletion?.allComplete &&
    !(missionActivityPilot && activityNavState?.stepId === activeStepId &&
      activityNavState.index < activityNavState.total - 1)
    ? withSwanNavigation(currentJourneyContext?.activity_progress, navigation)
        ?.next_action?.instruction ||
      `Required activities are recorded. Click “${navigation.label}” when ready.`
    : null;
  const phaseRecapVisible = unifiedSwanPanel && missionActivityPilot && sarasOpen &&
    phaseReviewAnswer?.fromIndex === index;
  const displayedCoach = coachAnnouncement
    ? pendingTransition?.fromIndex === index
      ? {
          title: pendingTransition.status === "loading"
            ? "Professor Swan is thinking"
            : "Let's learn from this phase",
          actionLabel: "PROFESSOR SWAN'S FEEDBACK",
          action: coachAnnouncement,
        }
      : {
          title: coachState?.title || "Welcome, Engineer!",
          actionLabel: "PROFESSOR SWAN",
          action: coachAnnouncement,
        }
    : completedPhaseCoachAction && coachState
      ? { ...coachState, action: completedPhaseCoachAction }
      : coachState;

  // Keep the pending-review message visible until the answer is ready;
  // once it appears in chat, do not repeat it in the guidance card.
  const visibleCoach = phaseRecapVisible && displayedCoach
    ? { ...displayedCoach, action: null }
    : displayedCoach;
  if (unifiedSwanPanel && sarasOpen) swanConversationOpenedRef.current = true;
  const preserveEmbeddedConversation =
    unifiedSwanPanel && swanConversationOpenedRef.current;
  const swanAskPanel = (sarasOpen || preserveEmbeddedConversation) ? (
    <PresentSarasPanel
      key={unifiedSwanPanel
        ? `${activeStepId}:${missionActivityPilot
            ? currentJourneyContext?.activity_progress?.focused_activity?.id || "start"
            : "phase"}:${phaseRecapVisible ? "review" : "help"}`
        : "overlay"}
      embedded={unifiedSwanPanel}
      focusedConversation={focusedSwanConversation}
      active={sarasOpen}

      initialQuestion={phaseRecapVisible ? "Let's learn from this phase" : sarasQuestion}
      initialAnswer={phaseRecapVisible ? phaseReviewAnswer.text : null}
      questions={isSelfGuidedFocus
        ? ["What should I do next?", "Give me a hint."]
        : (sarasContext?.suggested_questions ||
           currentJourneyContext?.suggested_questions || [])}
      colors={colors}
      type={type}
      onClose={closeSaras}
      askSaras={askSarasWithJourneyContext}
      userId={userId}
      language={sarasLanguage}
      assistantName={isSelfGuidedFocus ? "Professor Swan" : "Saras"}
      conversationMode={isSelfGuidedFocus}
      coachingScope={isSelfGuidedFocus
        ? `${activeStepId}:${missionActivityPilot
            ? currentJourneyContext?.activity_progress?.focused_activity?.id || ""
            : currentJourneyContext?.activity_progress?.inquiry_guard?.block_id || ""}`
        : null}
      streamAudioAllowed={!currentJourneyContext?.activity_progress?.inquiry_guard?.protect_answer}
      autoReadDefault={isSelfGuidedFocus}
      autoReadEnabled={
        isSelfGuidedFocus ? swanReadAloudEnabled : undefined
      }
      onAutoReadEnabledChange={
        isSelfGuidedFocus ? setSwanReadAloudEnabled : undefined
      }
      onReadAloudSpeakingChange={
        isSelfGuidedFocus ? setAiPanelSpeaking : undefined
      }
      onThinkingChange={
        isSelfGuidedFocus ? setAiPanelThinking : undefined
      }
      t={t}
    />
  ) : null;

  if (!journey) {
    return (
      <View style={[styles.empty, { backgroundColor: colors.pageBg }]}>
        <Text style={[type.body, { color: colors.textSecondary }]}>
          {t("No Journey has been loaded.")}
        </Text>
      </View>
    );
  }

  return (
    <View nativeID={isSelfGuidedFocus ? "wonderlabs-density-surface" : undefined}
      style={[styles.root, { backgroundColor: colors.pageBg }]}>
      <View style={[styles.topBar, { borderBottomColor: colors.border, paddingHorizontal: width < 900 ? 8 : 24, gap: width < 900 ? 6 : 16 }]}>
        <View style={styles.topLeft}>
          <View
            style={[
              styles.phaseDot,
              { backgroundColor: colors.accentPrimary },
            ]}
          />
          <Text
            style={[
              styles.phaseLabel,
              width < 900 ? { display: "none" } : null,
              type.chrome,
              { color: colors.accentPrimary },
            ]}
          >
            {phase}
          </Text>
          <Text style={[type.chrome, { color: colors.textMuted }]}>
            {t("Phase")} {total > 0 ? index + 1 : 0} {t("of")} {total}
          </Text>
          {showPhaseDots && !isSelfGuidedFocus ? (
            <View
              style={styles.phaseDots}
              accessibilityLabel={`${t("Phase")} ${index + 1} ${t("of")} ${total}`}
            >
              {Array.from({ length: total }, (_, phaseIndex) => (
                <View
                  key={phaseIndex}
                  style={[
                    styles.phaseMiniDot,
                    {
                      borderColor: phaseIndex <= index ? colors.accentPrimary : colors.border,
                      backgroundColor: phaseIndex <= index ? colors.accentPrimary : "transparent",
                      opacity: phaseIndex < index ? 0.55 : 1,
                    },
                    phaseIndex === index ? styles.phaseMiniDotCurrent : null,
                  ]}
                />
              ))}
            </View>
          ) : null}
        </View>

        <View style={[styles.titleWrap, width < 900 ? { display: "none" } : null]}>
          <Text
            numberOfLines={1}
            style={[
              styles.journeyTitle,
              type.chrome,
              { color: colors.textMuted },
            ]}
          >
            {title}
          </Text>
          {!!presentationLabel && (
            <Text
              numberOfLines={1}
              style={[
                styles.presentationLabel,
                { color: readOnly ? colors.textMuted : colors.accentPrimary },
              ]}
            >
              {presentationLabel}
            </Text>
          )}
        </View>

        <View style={styles.topActions}>
          {isSelfGuidedFocus && onMissionHome ? (
            <ChromeButton
              colors={colors}
              type={type}
              icon="home-outline"
              label={t("Mission Home")}
              onPress={onMissionHome}
            />
          ) : null}
          {focusedSwanConversation ? (
            <ChromeButton
              colors={colors}
              type={type}
              icon="chevron-left"
              label={t("Back to activity")}
              onPress={closeSaras}
            />
          ) : null}
          {!unifiedSwanPanel && !missionActivityPilot ? (
            <ChromeButton
              colors={colors}
              type={type}
              icon="robot-happy-outline"
              label={t(isSelfGuidedFocus ? "Ask Professor Swan" : "Ask Saras")}
              active={sarasOpen}
              accent={sarasOpen ? colors.accentPrimary : undefined}
              onPress={toggleSaras}
            />
          ) : null}
          <ChromeButton
            colors={colors}
            type={type}
            icon="close"
            label={t(exitLabel || (isSelfGuidedFocus ? "Exit guide" : "Exit"))}
            onPress={onExit}
          />
        </View>
      </View>

      {isSelfGuidedFocus ? (
        <View style={{ zIndex: 30 }}>
          <WonderLabsProgressNavigator
            label={t("Your Journey")}
            currentIndex={index}
            items={steps.map((step, phaseIndex) => ({
              id: step?.id || String(phaseIndex),
              title: t(step?.title || step?.name || `Step ${phaseIndex + 1}`),
              shortLabel: getStepPhase(step, phaseIndex, t),
              // Earlier phases have been passed through the Journey's existing
              // progression gate. Do not infer mastery of future phases.
              complete: phaseIndex < index,
              locked: phaseIndex > Math.max(index, visitedThroughIndex),
            }))}
            onSelect={(targetIndex) => goTo(targetIndex, { intent: "manual" })}
            expanded={journeyMapOpen}
            onExpandedChange={setJourneyMapOpen}
            accent={colors.accentPrimary}
            border={colors.border}
            text={colors.textPrimary}
            muted={colors.textMuted}
            surface={colors.surface}
            background={colors.pageBg}
            compact={compactWonderLabsViewport}
          />
        </View>
      ) : null}

      {needsSpeechStart && !missionActivityPilot ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            paddingHorizontal: 16,
            paddingVertical: 10,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            backgroundColor: colors.surface,
          }}
        >
          <Text style={[type.chrome, { color: colors.textPrimary }]}>
            {t("Professor Swan is ready to guide you from this phase.")}
          </Text>
          <TouchableOpacity
            onPress={() => {
              // Chrome is most reliable when the first *audible* utterance is
              // queued directly from the learner's click. Merely priming the
              // engine and speaking later from a React effect can lose the
              // browser's user-activation window.
              startProfessorSwan();

              let opening = "";
              if (coachState) {
                if (hasAnySavedWork) {
                  opening = coachState.complete
                    ? `Welcome back. You are on Phase ${index + 1}, and this part is already complete. You can review it or continue when you are ready.`
                    : `Welcome back. ${coachState.action || "Let’s continue where you stopped."}`;
                } else {
                  opening = [
                    coachState.welcome || "Welcome, Future Engineer!",
                    coachState.action,
                  ]
                    .filter(Boolean)
                    .join(" ");
                }
              } else {
                // The child renderer can still be finishing its first context
                // report when the learner clicks. Speak something now so the
                // browser is genuinely unlocked; the normal coach effect can
                // provide the detailed cue once coachState arrives.
                opening = `Professor Swan is ready. Let’s continue from Phase ${index + 1}.`;
              }

              if (String(opening || "").trim()) {
                setCoachAnnouncement(opening);
                if (coachState) {
                  initialWelcomeDoneRef.current = true;
                  narratedCoachKeyRef.current = `${index}:${coachState.action || ""}`;
                }
                swanSpeech.speak(opening, {
                  immediate: true,
                  onEnd: () => setCoachAnnouncement(""),
                  onError: () => setCoachAnnouncement(""),
                });
              }
            }}
            accessibilityLabel={t("Start Professor Swan")}
            style={{
              borderRadius: 999,
              paddingHorizontal: 16,
              paddingVertical: 8,
              backgroundColor: colors.accentPrimary,
            }}
          >
            <Text style={[type.chrome, { color: "#ffffff", fontWeight: "800" }]}>
              {t("Start Professor Swan")}
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={styles.stageArea}>
        {showCoachRail ? (
          <ProfessorSwanCoachRail
            coach={visibleCoach}
            colors={colors}
            type={type}
            readAloud={swanReadAloudEnabled}
            speaking={swanSpeech.speaking || aiPanelSpeaking}
            thinking={!swanSpeech.speaking && !aiPanelSpeaking &&
              (aiPanelThinking || (pendingTransition?.fromIndex === index && pendingTransition?.status === "loading"))}
            celebrating={pendingTransition?.fromIndex === index && pendingTransition?.status === "ready"}
            onToggleReadAloud={() =>
              setSwanReadAloudEnabled((current) => !current)
            }
            onAsk={() => openProfessorSwanHelp(currentJourneyContext)}
            onShowActivity={showCurrentActivity}
            collapsed={swanGuideCollapsed}
            onToggleCollapsed={() => {
              if (!swanGuideCollapsed) closeSaras();
              setSwanGuideCollapsed((current) => !current);
            }}
            unified={unifiedSwanPanel}
            conversationOpen={unifiedSwanPanel && sarasOpen}
            conversationPanel={unifiedSwanPanel ? swanAskPanel : null}
            narrowFullWidth={swanOccupiesSmallViewport}
            smallViewportExpanded={swanOccupiesSmallViewport}
            focusedConversation={focusedSwanConversation}
            allowFocusToggle={width >= 760}
            onToggleConversationFocus={() => setSwanFocusRequested((current) => !current)}
            compactHeight={height < 900}
            compactWidth={compactWonderLabsViewport}
            onCloseConversation={closeSaras}
            t={t}
          />
        ) : null}

        <Animated.View
          nativeID={isSelfGuidedFocus ? "swan-journey-stage" : undefined}
          style={[
            styles.stage,
            { opacity },
            showCoachRail ? styles.stageWithCoach : null,
            swanOccupiesSmallViewport ? { display: "none" } : null,
          ]}
        >
          <JourneyPanel
          journey={journey}
          authToken={authToken}
          lang={lang}
          isDarkMode={isDarkMode}
          activeStepIndex={index}
          onStepChange={goTo}
          handleVideoClick={handleVideoClick}
          handlePWOClick={handlePWOClick}
          onAsk={askQuestionFromJourney}
          onContextChange={setActiveJourneyContext}
          onActivityNavigationChange={missionActivityPilot ? registerActivityNavigation : undefined}
          sessionState={sessionState}
          activityCursorRef={activityCursorRef}
          onActivityCursorChange={onActivityCursorChange}
          onSessionStateChange={onSessionStateChange}
          readOnly={readOnly}
          journeyMode={isSelfGuidedFocus ? "self_guided" : "teacher_led"}
          onPageCompletionChange={
            isSelfGuidedFocus ? setPageCompletion : undefined
          }
          onOpenJourneyHelp={
            isSelfGuidedFocus ? openProfessorSwanHelp : undefined
          }
          onSelfGuidedCoachChange={
            isSelfGuidedFocus ? setCoachState : undefined
          }
          onMasterTeacherReadAloud={isSelfGuidedFocus ? readMasterTeacherInstructions : undefined}
          swanReadAloudEnabled={
            isSelfGuidedFocus ? swanReadAloudEnabled : false
          }
          presentationMode
          compactSelfGuidedLayout={unifiedSwanPanel}
          wonderLabsDensity={displayDensity}
          t={t}
        />
        </Animated.View>

        {sarasOpen && !unifiedSwanPanel ? (
          <View style={styles.sarasOverlay}>
            {swanAskPanel}
          </View>
        ) : null}
      </View>

      <View style={[styles.bottomWrap, focusedSwanConversation ? { display: "none" } : null]}>
        <View style={[styles.progressTrack, { backgroundColor: colors.border }]}>
          <View
            style={[
              styles.progressFill,
              {
                backgroundColor: colors.accentPrimary,
                width: `${progress}%`,
              },
            ]}
          />
        </View>

        <View style={styles.bottomBar}>
          <ChromeButton
            colors={colors}
            type={type}
            icon="chevron-left"
            label={t(missionActivityPilot && activityNavState?.stepId === activeStepId &&
              (activityNavState.index > 0 || activityNavState.beatIndex > 0)
                ? activityNavState.previousLabel || "Previous activity" : "Back")}
            disabled={atStart && !(missionActivityPilot && activityNavState?.stepId === activeStepId &&
              (activityNavState.index > 0 || activityNavState.beatIndex > 0))}
            onPress={goPrevious}
          />

          <View style={styles.keyHintWrap}>
            <Text
              style={[
                styles.keyHint,
                type.chrome,
                { color: colors.textMuted },
              ]}
            >
              {isSelfGuidedFocus
                ? sarasOpen && !unifiedSwanPanel
                  ? t("Close Professor Swan when you are ready to continue")
                  : pendingTransition?.fromIndex === index
                    ? pendingTransition.status === "loading"
                      ? t("Professor Swan is reviewing your work. Please wait a moment.")
                      : t(index >= total - 1
                          ? "Swan's review is optional. Finish Adventure when you are ready."
                          : "Swan's follow-up is optional. Choose Next phase when you are ready.")
                    : continueNotice
                      ? t(continueNotice)
                      : missionActivityPilot && activityNavState?.stepId === activeStepId &&
                        activityNavState.beatPending
                        ? activityNavState.canAdvance
                          ? t(`Up next: ${activityNavState.nextTitle || "the next part"}`)
                          : t(`Complete this part: ${activityNavState.missingLabel || "record the required evidence"}`)
                      : missionActivityPilot && activityNavState?.stepId === activeStepId &&
                        activityNavState.index < activityNavState.total - 1
                      ? activityNavState.canAdvance
                        ? t("Ready for the next activity when you are.")
                        : t("Use the activity above to record your evidence.")
                      : pageCompletion?.allComplete
                      ? t("Ready for a short Swan recap before the next phase.")
                      : pageCompletion?.firstIncomplete?.label
                        ? ""
                        : t("Professor Swan is checking this step…")
                : sarasOpen
                  ? t("S or Esc closes Saras · ← → changes phase")
                  : t("← → or a clicker to move · Space/Enter for next · S for Saras · Esc to exit")}
            </Text>
          </View>

          <ChromeButton
            colors={colors}
            type={type}
            icon="chevron-right"
            label={t(missionActivityPilot && activityNavState?.stepId === activeStepId &&
              (activityNavState.index < activityNavState.total - 1 || activityNavState.beatPending)
                ? activityNavState.canAdvance
                  ? activityNavState.nextLabel || "Next activity" : "See what’s missing"
                : missionActivityPilot && navigation.disabled && !pageCompletion?.allComplete
                  ? "See what’s missing" : navigation.label)}
            disabled={missionActivityPilot ? false :
              (activityNavState?.stepId === activeStepId &&
              (activityNavState.index < activityNavState.total - 1 || activityNavState.beatPending)
                ? !activityNavState.canAdvance : navigation.disabled)}
            onPress={goNext}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    minHeight: 0,
  },
  empty: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  topLeft: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
  },
  phaseDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    marginRight: 8,
  },
  phaseLabel: {
    fontWeight: "800",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginRight: 14,
  },
  phaseDots: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: 18,
  },
  phaseMiniDot: {
    width: 9,
    height: 9,
    borderRadius: 6,
    borderWidth: 1.5,
  },
  phaseMiniDotCurrent: {
    width: 13,
    height: 13,
    borderRadius: 7,
  },
  titleWrap: {
    flex: 1,
    minWidth: 0,
    alignItems: "center",
  },
  journeyTitle: {
    width: "100%",
    textAlign: "center",
    fontWeight: "600",
  },
  presentationLabel: {
    width: "100%",
    marginTop: 2,
    textAlign: "center",
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "700",
  },
  topActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  stageArea: {
    flex: 1,
    width: "100%",
    minHeight: 0,
    flexDirection: "row",
  },
  stage: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    minHeight: 0,
  },
  stageWithCoach: {
    width: "auto",
  },
  coachRail: {
    width: 224,
    minWidth: 224,
    marginLeft: 10,
    marginVertical: 10,
    borderWidth: 1,
    borderRadius: 16,
    padding: 10,
    alignSelf: "stretch",
    minHeight: 0,
    overflow: "hidden",
  },
  coachRailCollapsed: {
    width: 80,
    minWidth: 80,
    paddingHorizontal: 6,
  },
  coachCollapsedAskButton: {
    marginTop: 10,
    borderWidth: 1,
    borderRadius: 9,
    paddingVertical: 8,
    alignItems: "center",
  },
  // The same desktop width for guidance and chat avoids reflowing the stage.
  coachRailConversation: {
    width: 344,
    minWidth: 344,
  },
  coachRailFocusedWorkspace: {
    // Focused chat replaces the stage on short browser viewports.
    // It is not squeezed into the remaining space beside an activity.
    marginLeft: 10,
    marginRight: 10,
    paddingHorizontal: 12,
  },
  coachFocusedAvatarRow: { paddingVertical: 0, minHeight: 38 },
  coachRailMobileExpanded: {
    flex: 1,
    width: "auto",
    minWidth: 0,
    marginRight: 10,
  },
  coachRailMobileCollapsed: {
    width: 72,
    minWidth: 72,
    marginLeft: 6,
    paddingHorizontal: 4,
  },
  coachConversationActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    flexShrink: 0,
  },
  coachHideConversationButton: {
    paddingHorizontal: 8,
    paddingVertical: 7,
    borderWidth: 1,
    borderRadius: 9,
  },
  coachAvatarWrapConversation: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 10,
    paddingVertical: 4,
  },
  coachAvatarConversation: {
    width: 78,
    height: 90,
  },
  coachConversationIdentity: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  coachConversationGreeting: {
    fontSize: 15,
    fontWeight: "900",
    lineHeight: 20,
  },
  coachEmbeddedConversation: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    marginTop: 8,
  },
  coachEmbeddedConversationVisible: {
    borderTopWidth: 1,
    paddingTop: 8,
  },
  // Keep the current task visible during chat, but let long teacher feedback
  // scroll in this smaller area rather than stealing the whole conversation.
  coachActionWithConversation: {
    marginTop: 6,
    maxHeight: 118,
    flexShrink: 0,
  },
  coachHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
  },
  coachCollapseButton: {
    width: 26,
    height: 26,
    borderWidth: 1,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  coachAvatarWrap: {
    alignItems: "center",
  },
  coachAvatar: {
    width: 108,
    height: 128,
  },
  coachAvatarCollapsed: {
    width: 60,
    height: 84,
  },
  coachAvatarSpeaking: {
    opacity: 0.98,
  },
  coachSpeakingRow: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  coachSpeakingText: {
    fontSize: 11,
    fontWeight: "900",
  },
  coachEyebrow: {
    fontWeight: "900",
    letterSpacing: 0.4,
    fontSize: 10,
    flexShrink: 1,
  },
  coachTitle: {
    marginTop: 6,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: "900",
  },
  coachAction: {
    marginTop: 12,
    borderWidth: 1,
    borderRadius: 12,
    // The rail has a bounded viewport. Scroll long AI feedback inside its card
    // rather than letting it cover the fixed Journey navigation footer.
    flexShrink: 1,
    minHeight: 0,
    maxHeight: "58%",
  },
  coachActionContent: {
    padding: 12,
  },
  coachActionLabel: {
    fontSize: 11,
    fontWeight: "900",
    letterSpacing: 0.7,
  },
  coachActionText: {
    marginTop: 5,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
  },
  coachButtons: {
    marginTop: 12,
    gap: 8,
    flexShrink: 0,
  },
  coachButton: {
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 9,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  sarasOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "32%",
    minWidth: 360,
    maxWidth: 520,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  bottomWrap: {
    width: "100%",
  },
  progressTrack: {
    height: 3,
    width: "100%",
  },
  progressFill: {
    height: 3,
  },
  bottomBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  keyHintWrap: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 16,
  },
  keyHint: {
    textAlign: "center",
    fontWeight: "500",
  },
});
